import { useEffect, lazy, Suspense } from "react";
import { useParams, Outlet } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { LANGUAGE_LABELS, type Language, type Currency } from "@/lib/i18n";
import SeoHead from "@/components/SeoHead";

const SUPPORTED_LANGS = Object.keys(LANGUAGE_LABELS) as Language[];

// Talen die actief geindexeerd worden — alle andere krijgen noindex.
// Uitbreiden per fase: fase 2 voeg "de", "fr" toe na 200+ geindexeerde paginas.
const ACTIVE_LANGUAGES: Language[] = ["nl", "en"];

/** Maps language prefix to default currency */
const LANG_CURRENCY_MAP: Partial<Record<Language, Currency>> = {
  en: "GBP",
  nl: "EUR",
  de: "EUR",
  fr: "EUR",
  es: "EUR",
  it: "EUR",
  pt: "BRL",
  sv: "SEK",
  pl: "PLN",
  ja: "JPY",
  ko: "KRW",
  ru: "EUR",
  tr: "TRY",
  da: "EUR",
  no: "NOK",
  fi: "EUR",
  cs: "EUR",
};

/**
 * Layout route that reads an optional /:lang URL prefix and syncs
 * the I18n context (language + currency). Also sets the HTML lang attribute.
 *
 * SEO: talen buiten ACTIVE_LANGUAGES krijgen noindex, follow zodat Google
 * ze niet indexeert maar wel kan volgen voor interne links.
 */
export default function LangLayout() {
  const { lang } = useParams<{ lang: string }>();
  const { setLanguage, setCurrency, language } = useI18n();

  const isValidLang = !!lang && SUPPORTED_LANGS.includes(lang as Language);
  const isActiveLang = !!lang && ACTIVE_LANGUAGES.includes(lang as Language);

  useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang as Language) && lang !== language) {
      setLanguage(lang as Language);
      const defaultCurrency = LANG_CURRENCY_MAP[lang as Language];
      if (defaultCurrency) {
        setCurrency(defaultCurrency);
      }
    }
  }, [lang, language, setLanguage, setCurrency]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  if (!isValidLang) {
    const NotFound = lazy(() => import("@/pages/NotFound"));
    return (
      <Suspense fallback={null}>
        <NotFound />
      </Suspense>
    );
  }

  return (
    <>
      {!isActiveLang && (
        <SeoHead
          title=""
          description=""
          noindex={true}
        />
      )}
      <Outlet />
    </>
  );
}
